import {
  CHART_CHROME,
  ChartResult,
  dataNotes,
  formatNumber,
  getSeriesColors,
} from '@employ-engage/data';
import classNames from 'classnames';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  ErrorBar,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartMode } from '../hooks/useChartMode';
import InlineLoadingSpinner from '../inline-loading-spinner/inline-loading-spinner';
import { DataNotes } from '../info-note/data-notes';
import { CATEGORY_KEY, ERROR_KEY, toRecharts } from './to-recharts';

export type ChartVizType =
  | 'bar'
  | 'horizontalBar'
  | 'stackedBar'
  | 'line'
  | 'pie'
  | 'donut';

export interface ChartProps {
  result?: ChartResult | null;
  type?: ChartVizType;
  /** Parent must bound the height; ResponsiveContainer renders 0x0 otherwise. */
  height?: number | `${number}%`;
  valueFormat?: 'integer' | 'percent0' | 'percent1';
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  /** Show the denominator caption. On by default; off inside a small tile. */
  showBase?: boolean;
  /**
   * Called with the category a reader clicked, for cross-filtering.
   *
   * Absent means the chart is not interactive, and it then renders without a
   * pointer cursor — a chart that looks clickable and is not is worse than
   * one that plainly is not.
   */
  onSelect?: (category: string) => void;
  className?: string;
}

/**
 * How wide a single bar is allowed to get.
 *
 * Without a ceiling, a chart of two categories draws two slabs half the card
 * wide. That reads as a design, not as a measurement, and it exaggerates the
 * difference between them: the eye compares areas, and area grows with width
 * as well as length.
 */
const MAX_BAR_SIZE = 56;

/**
 * Gridlines run across the value axis only.
 *
 * A gridline exists to let the eye carry a value back to the axis. Lines
 * along the *category* axis separate categories that are already separated by
 * being different bars, so they add ink and no information.
 *
 * Which orientation that is flips with the chart: `horizontalBar` is recharts
 * `layout="vertical"`, where the value axis is X, so the useful lines are the
 * vertical ones. Getting this backwards is invisible in jsdom and obvious on
 * screen.
 */
const gridOrientation = (horizontal: boolean) =>
  horizontal
    ? { horizontal: false, vertical: true }
    : { horizontal: true, vertical: false };

const Centered = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-full w-full items-center justify-center p-4 text-center text-sm text-light-secondaryText dark:text-dark-secondaryText">
    {children}
  </div>
);

/**
 * Renders the reporting engine's result.
 *
 * States are checked in order — error, loading, withheld, empty, chart — so a
 * withheld result can never fall through to a plot.
 *
 * A suppressed cell arrives as null and is drawn as a gap. It is never
 * coerced to zero: zero would read as "nobody said this", which turns a
 * privacy control into a false data point.
 */
export function Chart({
  result,
  type = 'bar',
  height = '100%',
  valueFormat = 'integer',
  loading,
  error,
  emptyMessage,
  showLegend,
  showGrid = true,
  showBase = true,
  onSelect,
  className,
}: ChartProps) {
  const { t } = useTranslation(['common']);
  // The mode is read from the element's own ancestors, so a chart is themed
  // by where it is rendered rather than by a prop every caller must remember.
  const rootRef = useRef<HTMLDivElement>(null);
  const mode = useChartMode(rootRef);
  const chrome = CHART_CHROME[mode];
  const { rows, series, hasIntervals, countOnly } = useMemo(
    () => toRecharts(result, mode),
    [result, mode]
  );

  const tooltipProps = {
    contentStyle: {
      background: chrome.tooltipSurface,
      border: `1px solid ${chrome.tooltipBorder}`,
      borderRadius: 8,
      boxShadow: chrome.tooltipShadow,
      fontSize: 12,
      color: chrome.tooltipText,
      padding: '6px 10px',
    },
    labelStyle: {
      color: chrome.tooltipText,
      fontWeight: 600,
      marginBottom: 2,
    },
    // Each row keeps its series colour, which is how a reader maps a number
    // back to a line or a slice.
    itemStyle: { padding: 0 },
  };

  const isPie = type === 'pie' || type === 'donut';

  /**
   * A pie's slices are its rows, not its series.
   *
   * Everything else here plots rows along an axis and distinguishes the
   * *series* by colour, so both the palette and the legend were decided by
   * the series. A pie has no axis: its rows are the only thing on screen, and
   * with one series — which is every pie without a breakdown — that meant one
   * colour for every slice and no legend to name them. A solid disc.
   *
   * Above the early returns, with the other hook: a chart that starts empty
   * and then has data must not change how many hooks it runs.
   */
  const sliceColors = useMemo(
    () =>
      isPie
        ? getSeriesColors(
            rows.map((row) => String(row[CATEGORY_KEY] ?? '')),
            mode
          )
        : [],
    [isPie, rows, mode]
  );

  if (error) {
    return (
      <Centered>
        <span className="text-semantic-error">{error}</span>
      </Centered>
    );
  }

  if (loading) {
    return (
      <Centered>
        <InlineLoadingSpinner />
      </Centered>
    );
  }

  // Checked before "empty" on purpose: a withheld chart is not an empty one,
  // and saying so is the difference between "no data" and "protected".
  if (result?.meta?.suppressedAll) {
    return (
      <Centered>
        <div>
          <i className="fa-solid fa-lock mb-2 block text-lg" aria-hidden />
          {t(
            'Not enough responses yet to show this safely. At least {{count}} are needed.',
            { count: result.meta.minCellCount }
          )}
        </div>
      </Centered>
    );
  }

  if (!rows.length || !series.length) {
    return (
      <Centered>{emptyMessage ?? t('No responses in this range yet')}</Centered>
    );
  }

  // A pie names its slices in the legend or nowhere at all, so it is counted
  // on rows like its colours are.
  const legend = showLegend ?? (isPie ? rows.length > 1 : series.length > 1);

  /**
   * A percentage whose denominator is invisible is how this product came to
   * report 16% where the truth was 60%. When a question does not apply to
   * everyone, the base is not decoration — it is the number that makes the
   * percentage mean anything.
   */
  const Base = () => {
    if (!showBase || !result?.meta) return null;
    const { baseRespondents, respondentsInScope, notApplicableRespondents } =
      result.meta;
    if (baseRespondents === undefined || baseRespondents === null) return null;

    const excluded = notApplicableRespondents > 0;
    return (
      <div className="mt-1 px-1">
        <p className="text-xs text-light-secondaryText dark:text-dark-secondaryText">
          {excluded
            ? t('{{base}} of {{total}} responses where this applied', {
                base: baseRespondents,
                total: respondentsInScope,
              })
            : t('{{count}} responses', { count: baseRespondents })}
        </p>
        {/* The base above is the denominator and always shows; these are the
            caveats, and their reasoning lives behind the note. */}
        <DataNotes
          notes={dataNotes(result, { hasIntervals, countOnly })}
          layout="inline"
          // A comparison that did not apply is shown, not hidden behind the
          // note: the reader turned a control on and needs to see that
          // nothing came of it. The rest stay behind the ⓘ.
          showShort={['countOnly', 'intervals', 'comparisonUnavailable']}
        />
      </div>
    );
  };
  /**
   * The legend's label text, in body colour rather than its series colour.
   *
   * Recharts colours the label by its series. On the lighter palette slots
   * that is text at 3:1 or worse against a white card — the colour was
   * validated as a fill for a bar, which is a solid block, not as text, which
   * is thin strokes with gaps. The swatch keeps the colour, doing the job it
   * was chosen for.
   *
   * Done with `formatter` rather than by replacing the legend wholesale:
   * recharts measures its own legend to decide how much of the box the plot
   * gets, and a custom `content` it cannot measure collapses the plot — a pie
   * ends up with a radius of zero and draws no slices at all. Confirmed, not
   * assumed: chart-pie.spec.tsx fails that way.
   */
  /**
   * Recharts hands a click the row it was drawn from, but through different
   * props depending on the mark: a bar gets the row itself, a pie slice gets
   * it nested under `payload`. Reading both is cheaper than one handler per
   * chart type.
   */
  const selectHandler = onSelect
    ? (entry: unknown) => {
        const row = entry as Record<string, unknown> & {
          payload?: Record<string, unknown>;
        };
        const category = row?.[CATEGORY_KEY] ?? row?.payload?.[CATEGORY_KEY];
        if (typeof category === 'string' && category) onSelect(category);
      }
    : undefined;

  const interactive = onSelect ? 'cursor-pointer' : '';

  const legendFormatter = (value: string) => (
    <span className="text-xs" style={{ color: chrome.legendText }}>
      {value}
    </span>
  );

  // Recharts types a tooltip value as string | number | (string | number)[],
  // so the formatter has to accept that and narrow, rather than declaring the
  // number it expects and being cast into place at three call sites.
  const formatter = (value: unknown) =>
    formatNumber(typeof value === 'number' ? value : null, valueFormat);
  const horizontal = type === 'horizontalBar';
  const stacked = type === 'stackedBar';

  if (isPie) {
    return (
      <div ref={rootRef} className={classNames('h-full w-full', className)}>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={rows}
              dataKey={series[0].key}
              nameKey={CATEGORY_KEY}
              innerRadius={type === 'donut' ? '55%' : 0}
              outerRadius="80%"
              paddingAngle={1}
              onClick={selectHandler}
              className={interactive}
            >
              {rows.map((row, i) => (
                <Cell key={i} fill={sliceColors[i]} stroke="none" />
              ))}
            </Pie>
            <Tooltip {...tooltipProps} formatter={formatter} />
            {legend !== false && <Legend formatter={legendFormatter} iconType="square" iconSize={10} />}
          </PieChart>
        </ResponsiveContainer>
        <Base />
      </div>
    );
  }

  if (type === 'line') {
    return (
      <div ref={rootRef} className={classNames('h-full w-full', className)}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={rows}
            margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
          >
            {showGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={chrome.grid}
                {...gridOrientation(false)}
              />
            )}
            <XAxis
              dataKey={CATEGORY_KEY}
              tick={{ fontSize: 11 }}
              stroke={chrome.axis}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              stroke={chrome.axis}
            />
            <Tooltip {...tooltipProps} formatter={formatter} />
            {legend && <Legend formatter={legendFormatter} iconType="square" iconSize={10} />}
            {series.map((s) => (
              <Line
                key={s.key}
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                dot={{ r: 3 }}
                // A withheld or missing point stays a gap rather than being
                // bridged, which would invent a trend that is not in the data.
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        <Base />
      </div>
    );
  }

  return (
    <div ref={rootRef} className={classNames('h-full w-full', className)}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={rows}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={
            horizontal
              ? { top: 8, right: 16, left: 8, bottom: 0 }
              : { top: 8, right: 8, left: -16, bottom: 0 }
          }
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chrome.grid}
              {...gridOrientation(horizontal)}
            />
          )}
          {horizontal ? (
            <>
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{ fontSize: 11 }}
                stroke={chrome.axis}
              />
              <YAxis
                type="category"
                dataKey={CATEGORY_KEY}
                tick={{ fontSize: 11 }}
                stroke={chrome.axis}
                width={150}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey={CATEGORY_KEY}
                tick={{ fontSize: 11 }}
                stroke={chrome.axis}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11 }}
                stroke={chrome.axis}
              />
            </>
          )}
          <Tooltip
            {...tooltipProps}
            formatter={formatter}
            cursor={{ fill: chrome.cursor }}
          />
          {legend && <Legend formatter={legendFormatter} iconType="square" iconSize={10} />}
          {series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={s.color}
              stackId={stacked ? 'a' : undefined}
              radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
              maxBarSize={MAX_BAR_SIZE}
              onClick={selectHandler}
              className={interactive}
            >
              {/* The width of this bar is the honest answer at a small sample
                  size: it says "we cannot tell these apart" without needing
                  any statistics to read. */}
              {hasIntervals && (
                <ErrorBar
                  dataKey={ERROR_KEY}
                  width={4}
                  strokeWidth={1.5}
                  stroke={chrome.interval}
                  direction={horizontal ? 'x' : 'y'}
                />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      <Base />
    </div>
  );
}

export default Chart;
